// src/pages/healthcare/appointments/AppointmentsPage.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { Alert, Button, Empty, Space, Segmented } from "antd";
import { PlusOutlined, PlayCircleOutlined, CalendarOutlined, LeftOutlined, RightOutlined, ClockCircleOutlined, UnorderedListOutlined, AppstoreOutlined } from "@ant-design/icons";
import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import InlineLoading from "../../../components/shared/InlineLoading";

// Calendar utilities
import { format, startOfWeek, addDays } from "date-fns";

// Components
import AppointmentDrawer from "./AppointmentDrawer";
import BookingLinkModal from "./BookingLinkModal";
import StaffLegend from "./components/StaffLegend";
import PractitionerCalendar, { CalendarViewMode, resolveSlotMinutes } from "./components/PractitionerCalendar";
import SlotActionPopover, { SlotSelection } from "./components/SlotActionPopover";
import BlockOutTimeModal from "./components/BlockOutTimeModal";
import BlockMoveConfirm from "./components/BlockMoveConfirm";
import RescheduleConfirmModal from "./components/RescheduleConfirmModal";
import AddAvailabilityModal from "./components/AddAvailabilityModal";

// Types
import { Appointment } from "../../../types/appointments/AppointmentTypes";
import { TeamMember, defaultTeamMember, TimeBlock } from "../../../types/admin/TeamTypes";
import { User } from "../../../types/admin/UserTypes";
import { defaultPatient, Patient } from "../../../types/healthcare/PatientTypes";

// APIs
import { fetchAppointments } from "../../../api/healthcare/Appointment/AppointmentApi";
import PractitionerVideoConsultation from "../consultation/VideoConsultation";
import InPersonConsultation from "../consultation/InPersonConsultation";
import { fetchAllTeamMembers } from "../../../api/admin/teamApi";
import { fetchAppointmentSettings } from "../../../api/healthcare/Appointment/AppointmentSettingsApi";

// Hooks and utils
import { useSchedule } from "../../../hooks/appointment/useSchedule";
import { usePractitionerAvailability } from "../../../hooks/appointment/usePractitionerAvailability";
import { useUnavailableBlockMove } from "../../../hooks/appointment/useUnavailableBlockMove";
import { useAppointmentMove } from "../../../hooks/appointment/useAppointmentMove";
import { useVisiblePractitioners, CalendarPrefView } from "../../../hooks/appointment/useVisiblePractitioners";
import { DateTimeUtils } from "../../../utils/userPrefs/DateTimeUtils";
import { canManageOthersSchedule, hasAnyRoleInGroup } from "../../../utils/userPrefs/validatePermissions";
import { BlockRef } from "../../../utils/appointments/availability";
import { wallClockParts } from "../../../utils/schedule/wallClock";
import { extractRoomId } from "../../../utils/consult/consultationLink";
import type { UserUuid } from "../../../types/identity";

interface Event {
	id: number;
	title: string;
	start: Date;
	end: Date;
	resource: {
		appointment: Appointment;
	};
}

const AppointmentManagementPage: React.FC = () => {
	const user: User = getSessionUser();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [slotMinutes, setSlotMinutes] = useState<number>(15);

	// Drawers and modals
	const [isAppointmentDrawerOpen, setIsAppointmentDrawerOpen] = useState<boolean>(false);
	const [isConsultationModalOpen, setIsConsultationModalOpen] = useState<boolean>(false);
	const [isBookingLinkModalOpen, setIsBookingLinkModalOpen] = useState<boolean>(false);

	// A drag opens the action popover; a plain click keeps opening the appointment drawer.
	const [slotSelection, setSlotSelection] = useState<SlotSelection | null>(null);
	const [scheduleAction, setScheduleAction] = useState<"block" | "availability" | null>(null);
	const [blockEdit, setBlockEdit] = useState<BlockRef | null>(null);

	// Calendar
	const [date, setDate] = useState<Date>(new Date());

	// Appointment
	const [isImpromptu, setIsImpromptu] = useState<boolean>(false);
	const [appointmentEvents, setAppointmentEvents] = useState<Event[]>([]);
	const [selectedAppointment, setSelectedAppointment] = useState<Appointment>();

	// Practitioner and patient selection
	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
	const [selectedPatient, setSelectedPatient] = useState<Patient>(defaultPatient);

	// The visible SET is the calendar filter. `selectedTeamMember` keeps its other jobs — schedule
	// source (the company fallback when null feeds the hour-range widening), the create pre-fill, the drawer and the consultation modal.
	const { visibleIds, ready: visibleIdsReady, onlyMineUnavailable, view, setView, toggle, showAll, showNone, showOnlyMine } = useVisiblePractitioners({ teamMembers, userId: user.id });

	// Bouncing to the list and back must land on the calendar view the user left, not on Day.
	const lastCalendarView = useRef<CalendarViewMode>("day");
	if (view !== "list") lastCalendarView.current = view;
	const calendarView: CalendarViewMode = lastCalendarView.current;

	const schedule = useSchedule(selectedTeamMember);

	const isCancelledStatus = (status?: string | null): boolean => {
		const normalised = (status ?? "").toLowerCase().replace(/_/g, " ").trim();
		return normalised === "cancelled" || normalised.startsWith("cancelled ");
	};
	

	// Cosmetic only — healthcare's schedule routes declare healthcare.schedules.manage and are the boundary.
	const canManageSchedule = useMemo(() => canManageOthersSchedule(), [user.id]);

	const blockMove = useUnavailableBlockMove();

	// Calculate week days based on current date
	const weekDays = useMemo(() => {
		const weekStart = startOfWeek(date, { weekStartsOn: 1 });
		return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
	}, [date]);

	// Calculate start and end dates for API call
	const { startDate, endDate } = useMemo(() => {
		const start = weekDays[0];
		const end = new Date(weekDays[6]);
		end.setHours(23, 59, 59, 999);
		return { startDate: start, endDate: end };
	}, [weekDays]);

	// The day view's columns: whoever the user has ticked.
	const displayTeamMembers = useMemo(() => teamMembers.filter((member) => visibleIds.has(member.id)), [teamMembers, visibleIds]);

	const practitionerIds = useMemo(() => displayTeamMembers.map((member) => member.id), [displayTeamMembers]);

	// Whether the signed-in user has a column of their own; reception does not.
	const onRoster = useMemo(() => teamMembers.some((member) => member.id === user.id), [teamMembers, user.id]);

	// D2: a week drag blocks the ticked set for a manager and only the user for anyone else.
	const weekBlockDefaults = useMemo(
		() => (canManageSchedule ? displayTeamMembers.map((member) => member.id) : onRoster ? [user.id] : []),
		[canManageSchedule, displayTeamMembers, onRoster, user.id]
	);

	// "YYYY-MM-DD" before the query key — a Date stringifies to a new value every render.
	const { availabilityFrom, availabilityTo } = useMemo(
		() => ({ availabilityFrom: format(weekDays[0], "yyyy-MM-dd"), availabilityTo: format(weekDays[6], "yyyy-MM-dd") }),
		[weekDays]
	);

	// overrides/blocks/clinicClosures are the calendar's shading; the rule that reads them lives in utils/appointments/availability.
	const { overrides, blocks, clinicClosures, isError: availabilityFailed } = usePractitionerAvailability(practitionerIds, availabilityFrom, availabilityTo);

	// 08:00-20:00, widened by any real block. maxHour seeded at 24 before, which wallClockParts can never exceed, so only minHour ever moved; both sources share widen() so neither can be fixed alone.
	const { minHour, maxHour } = useMemo(() => {
		let earliest = 8;
		let latest = 20;

		const widen = (dayBlocks: any) => {
			const blocks = Array.isArray(dayBlocks) ? dayBlocks : dayBlocks?.start ? [dayBlocks] : [];
			blocks.forEach((block: TimeBlock) => {
				const startTime = wallClockParts(block.start);
				if (startTime && startTime.hours < earliest) earliest = startTime.hours;

				const endTime = wallClockParts(block.end);
				if (endTime && endTime.hours > latest) latest = endTime.hours;
			});
		};

		displayTeamMembers.forEach((member) => {
			const memberSchedule = member.team_member_info?.availability_schedule;
			if (memberSchedule) Object.values(memberSchedule).forEach(widen);
		});

		if (schedule) Object.values(schedule).forEach(widen);

		return { minHour: earliest, maxHour: latest };
	}, [schedule, displayTeamMembers]);

	// Initial load
	useEffect(() => {
		const load = async () => {
			setIsLoading(true);
			await getPractitioners();
			setIsLoading(false);
		};
		load();
	}, []);

	// The clinic's calendar granularity; 15 minutes against a backend that predates the setting.
	useEffect(() => {
		fetchAppointmentSettings()
			.then((settings) => setSlotMinutes(resolveSlotMinutes(settings?.calendar_slot_minutes)))
			.catch((err) => console.error("Error fetching appointment settings:", err));
	}, []);

	// Reload appointments when the week changes
	useEffect(() => {
		if (startDate && endDate) {
			loadAppointments();
		}
	}, [startDate, endDate]);

	const loadAppointments = async () => {
		setIsLoading(true);
		try {
			// Fetch all appointments for the week — practitioner filtering is done client-side
			// so that tandem (secondary) practitioner assignments are included in the filter.
			const { data } = await fetchAppointments(startDate, endDate);

			const eventsLoading: Event[] = data
				// A cancellation frees its slot, so it is not drawn.
				.filter((appointment) => appointment.start_time && appointment.end_time && !isCancelledStatus(appointment.status))
				.map((appointment): Event | null => {
					let startTime: Date;
					let endTime: Date;

					if (appointment.start_time instanceof Date) {
						startTime = appointment.start_time;
					} else if (typeof appointment.start_time === "string") {
						const parsedStart = DateTimeUtils.parseUTCToLocal(appointment.start_time);
						if (!parsedStart) {
							console.error("Failed to parse start_time:", appointment.start_time);
							return null;
						}
						startTime = parsedStart;
					} else {
						console.error("Invalid start_time format:", appointment.start_time);
						return null;
					}

					if (appointment.end_time instanceof Date) {
						endTime = appointment.end_time;
					} else if (typeof appointment.end_time === "string") {
						const parsedEnd = DateTimeUtils.parseUTCToLocal(appointment.end_time);
						if (!parsedEnd) {
							console.error("Failed to parse end_time:", appointment.end_time);
							return null;
						}
						endTime = parsedEnd;
					} else {
						console.error("Invalid end_time format:", appointment.end_time);
						return null;
					}

					if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
						console.error("Invalid date objects for appointment:", appointment.id);
						return null;
					}

					return {
						id: appointment.id,
						title: appointment.title || "No Title",
						start: startTime,
						end: endTime,
						resource: {
							appointment: {
								...appointment,
								start_time: startTime,
								end_time: endTime,
							},
						},
					};
				})
				.filter((event): event is Event => event !== null)
				.sort((a, b) => a.start.getTime() - b.start.getTime());

			setAppointmentEvents(eventsLoading);
		} catch (error) {
			console.error("Error loading appointments:", error);
		} finally {
			setIsLoading(false);
		}
	};

	const getPractitioners = async (search?: string) => {
		try {
			// Fetches the WHOLE roster (paged past the server's 100-per-page cap), not a
			// single truncated page — see S6, 86d420tcg. The clinical-role filter below runs
			// over the complete set.
			const { data } = await fetchAllTeamMembers(search);

			let clinicians = [];
			for (const member of data) {
				if (hasAnyRoleInGroup("CLINICAL", member)) {
					clinicians.push(member);
				}
			}

			setTeamMembers(clinicians);
		} catch (err) {
			console.error("Error fetching professionals:", err);
		}
	};

	const practitionerName = useCallback(
		(id: UserUuid): string => {
			const member = teamMembers.find((candidate) => candidate.id === id);
			return member ? `${member.first_name} ${member.last_name}`.trim() : "That practitioner";
		},
		[teamMembers]
	);

	const appointmentMove = useAppointmentMove({ onMoved: loadAppointments, practitionerName });

	const createNewAppointment = (slotDate?: Date, practitionerId?: UserUuid, slotEnd?: Date) => {
		let appointmentData: Partial<Appointment>;

		if (slotDate instanceof Date) {
			appointmentData = {
				start_time: slotDate,
				end_time: slotEnd instanceof Date && slotEnd.getTime() > slotDate.getTime() ? slotEnd : new Date(slotDate.getTime() + 30 * 60000),
			};
		} else {
			appointmentData = {
				start_time: new Date(),
				end_time: new Date(new Date().getTime() + 30 * 60000),
			};
		}

		setSelectedAppointment({
			created_at: new Date(),
			id: 0,
			patient_id: "",
			practitioner_id: practitionerId || selectedTeamMember?.id || "",
			status: "Pending",
			updated_at: new Date(),

			title: "",
			category: "",
			location: "",
			room_number: "",
			...appointmentData,
		});
		setIsAppointmentDrawerOpen(true);
	};

	const startNewAppointment = () => {
		let app: Appointment = {
			id: 0,
			category: "Office Visit",
			created_at: new Date(),
			location: "In Person",
			patient_id: "",
			practitioner_id: user.id,
			room_number: "",
			status: "Started",
			title: "New Impromptu Consult",
			updated_at: new Date(),
			consultation_link: "",
			end_time: new Date(new Date().getTime() + 15 * 60000),
			start_time: new Date(),
		};
		setSelectedAppointment(app);
		setIsImpromptu(true);
		const practitionerForAppointment = user as TeamMember;
		setSelectedTeamMember(practitionerForAppointment || defaultTeamMember);
		setIsAppointmentDrawerOpen(true);
	};

	const editAppointment = (event: Event) => {
		setIsImpromptu(false);
		const appointment = event.resource.appointment;
		setSelectedAppointment(appointment);
		const practitionerForAppointment = teamMembers.find((p) => p.id === appointment.practitioner_id);
		setSelectedTeamMember(practitionerForAppointment || defaultTeamMember);
		setIsAppointmentDrawerOpen(true);
	};

	const handleCloseAppointmentDrawer = (id?: number) => {
		if (isImpromptu && id) {
			setIsAppointmentDrawerOpen(false);
			setIsConsultationModalOpen(true);
		} else {
			setSelectedTeamMember(null);
			setIsAppointmentDrawerOpen(false);
			loadAppointments();
		}
	};

	const handleStartConsultation = (appointment: Appointment) => {
		setSelectedAppointment(appointment);
		setIsAppointmentDrawerOpen(false);
		setIsConsultationModalOpen(true);
	};

	const closeScheduleAction = () => {
		setScheduleAction(null);
		setSlotSelection(null);
	};

	const handleNavigate = (direction: "prev" | "next") => {
		const step = calendarView === "day" ? 1 : 7;
		const newDate = new Date(date);
		newDate.setDate(date.getDate() + (direction === "next" ? step : -step));
		setDate(newDate);
	};

	// The dropped position, painted from the confirm until the reload lands; cleared on any error, which restores the original.
	const positionedEvents = useMemo(() => {
		const moved = appointmentMove.optimistic;
		if (!moved) return appointmentEvents;

		return appointmentEvents.map((event) =>
			event.id === moved.id
				? {
						...event,
						start: moved.start,
						end: moved.end,
						resource: { appointment: { ...event.resource.appointment, start_time: moved.start, end_time: moved.end, practitioner_id: moved.practitionerId } },
				  }
				: event
		);
	}, [appointmentEvents, appointmentMove.optimistic]);

	// `visibleIdsReady` gates this so the stored set cannot flash "none selected" before it hydrates.
	const noPractitionersVisible = visibleIdsReady && visibleIds.size === 0;

	const filteredAppointments = useMemo(() => {
		// A tandem appointment stays visible while any of its practitioners is ticked.
		return positionedEvents.filter((event) => {
			const appt = event.resource.appointment;
			if (visibleIds.has(appt.practitioner_id)) return true;
			return appt.practitioners?.some((p: any) => visibleIds.has(p.practitioner_id)) ?? false;
		});
	}, [positionedEvents, visibleIds]);

	return (
		<Layout>
			<PageContainer
				data-testid="appointments-root"
			>
				<PageToolbar
					primaryAction={{ label: "New Appointment", icon: <PlusOutlined />, onClick: () => createNewAppointment(), testId: "appointments-create-btn" }}
					extraActions={[
						{ label: "Start Now", icon: <PlayCircleOutlined />, onClick: startNewAppointment },
						{ label: "Today", icon: <CalendarOutlined />, onClick: () => setDate(new Date()), testId: "appointments-today-btn" },
					]}
					filters={[
						{
							type: "custom",
							render: (
								<Space size={8}>
									<>
										{/* PageToolbar does not forward testId on a custom filter, so the anchors sit on the labels. */}
										<Segmented
											value={calendarView}
											onChange={(value) => setView(value as CalendarPrefView)}
											options={[
												{ label: <span data-testid="appointments-view-day">Day</span>, value: "day" },
												{ label: <span data-testid="appointments-view-week-all">Week</span>, value: "week-all" },
											]}
										/>
										<Button icon={<LeftOutlined />} size="small" onClick={() => handleNavigate("prev")} data-testid="appointments-prev-btn" />
										<span data-testid="appointments-date-label" style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", padding: "0 8px", color: "var(--mg-text)" }}>
											{calendarView === "day" ? format(date, "EEE, MMM d, yyyy") : `${format(weekDays[0], "MMM d")} – ${format(weekDays[6], "MMM d, yyyy")}`}
										</span>
										<Button icon={<RightOutlined />} size="small" onClick={() => handleNavigate("next")} data-testid="appointments-next-btn" />
									</>
								</Space>
							),
						},
						{
							type: "custom",
							render: (
								<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>
									<ClockCircleOutlined style={{ marginRight: 4 }} />
									{DateTimeUtils.getUserTimezone()} ({DateTimeUtils.getTimezoneOffset()})
								</span>
							),
						},
					]}
				/>

				{availabilityFailed && (
					<Alert
						type="warning"
						showIcon
						style={{ marginTop: 16 }}
						message="Availability could not be loaded"
						description="Appointments are shown as normal, but practitioner unavailability and clinic closures are missing from the calendar."
						data-testid="appointments-availability-error"
					/>
				)}

				<div
					style={{
						display: "grid",
						gridTemplateColumns: "3fr 1fr",
						gap: 16,
					}}
				>
					{/* Main calendar / list area */}
					{isLoading ? (
						<InlineLoading message="Loading appointments…" minHeight={400} />
					) : noPractitionersVisible ? (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="No practitioners selected"
							style={{ padding: "64px 0", border: "1px solid var(--mg-border)", borderRadius: 8 }}
							data-testid="appointments-no-practitioners"
						>
							<Button type="primary" onClick={showAll} data-testid="appointments-show-all-practitioners">
								Show all
							</Button>
						</Empty>
					) : (
						<PractitionerCalendar
							events={filteredAppointments}
							teamMembers={displayTeamMembers}
							allTeamMembers={teamMembers}
							date={date}
							viewMode={calendarView}
							minHour={minHour}
							maxHour={maxHour}
							slotMinutes={slotMinutes}
							overrides={overrides}
							blocks={blocks}
							clinicClosures={clinicClosures}
							canManageSchedule={canManageSchedule}
							currentUserId={user.id ?? null}
							mutatingBlockKey={blockMove.movingKey}
							mutatingAppointmentId={appointmentMove.movingId}
							onEventClick={editAppointment}
							onSlotClick={createNewAppointment}
							onSlotSelect={setSlotSelection}
							onBlockEdit={setBlockEdit}
							onBlockMove={blockMove.requestMove}
							onAppointmentMove={appointmentMove.requestMove}
							onNavigate={setDate}
						/>
					)}

					{/* Right sidebar */}
					<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
						<StaffLegend
							teamMembers={teamMembers}
							appointments={filteredAppointments}
							visibleIds={visibleIds}
							onToggleVisible={toggle}
							onShowAll={showAll}
							onShowNone={showNone}
							onShowOnlyMine={showOnlyMine}
							onlyMineUnavailable={onlyMineUnavailable}
						/>
					</div>
				</div>
			</PageContainer>

			{slotSelection && scheduleAction === null && (
				<SlotActionPopover
					selection={slotSelection}
					canEditSchedule={canManageSchedule || slotSelection.practitionerId === user.id || (slotSelection.practitionerId === null && onRoster)}
					onNewAppointment={() => {
						const { start, end, practitionerId } = slotSelection;
						setSlotSelection(null);
						// A week drag has no column, but a single ticked practitioner is still an unambiguous pre-fill.
						createNewAppointment(start, practitionerId ?? (visibleIds.size === 1 ? [...visibleIds][0] : undefined), end);
					}}
					onBlockOutTime={() => setScheduleAction("block")}
					onAddAvailability={() => setScheduleAction("availability")}
					onClose={() => setSlotSelection(null)}
				/>
			)}

			{slotSelection && scheduleAction === "block" && (
				<BlockOutTimeModal
					selection={slotSelection}
					teamMembers={teamMembers}
					canManageSchedule={canManageSchedule}
					userId={user.id}
					defaultPractitionerIds={weekBlockDefaults}
					visibleIds={visibleIds}
					onClose={closeScheduleAction}
				/>
			)}

			{slotSelection && scheduleAction === "availability" && (
				<AddAvailabilityModal
					selection={slotSelection}
					teamMembers={teamMembers}
					canManageSchedule={canManageSchedule}
					defaultPractitionerId={slotSelection.practitionerId ?? (onRoster ? user.id : null)}
					userId={user.id}
					onClose={closeScheduleAction}
				/>
			)}

			{blockEdit && (
				<BlockOutTimeModal block={blockEdit} teamMembers={teamMembers} canManageSchedule={canManageSchedule} userId={user.id} onClose={() => setBlockEdit(null)} />
			)}

			{blockMove.pending && (
				<BlockMoveConfirm
					conflicts={blockMove.pending.conflicts}
					checkFailed={blockMove.pending.checkFailed}
					onConfirm={blockMove.confirmPending}
					onCancel={blockMove.cancelPending}
				/>
			)}

			{appointmentMove.pending && (
				<RescheduleConfirmModal
					pending={appointmentMove.pending}
					practitionerName={practitionerName}
					busy={appointmentMove.movingId !== null}
					onConfirm={appointmentMove.confirmPending}
					onCancel={appointmentMove.cancelPending}
				/>
			)}

			{isBookingLinkModalOpen && (
				<BookingLinkModal
					isOpen={isBookingLinkModalOpen}
					onClose={() => setIsBookingLinkModalOpen(false)}
					patient={selectedPatient}
				/>
			)}

			{isAppointmentDrawerOpen && selectedAppointment && (
				<AppointmentDrawer
					user={user}
					isOpen={isAppointmentDrawerOpen}
					onClose={handleCloseAppointmentDrawer}
					onStartConsultation={handleStartConsultation}
					selectedAppointment={selectedAppointment}
					setSelectedAppointment={setSelectedAppointment}
					teamMembers={teamMembers}
					selectedTeamMember={selectedTeamMember}
					setSelectedTeamMember={setSelectedTeamMember}
					selectedPatient={selectedPatient}
					setSelectedPatient={setSelectedPatient}
				/>
			)}

			{isConsultationModalOpen &&
				selectedAppointment &&
				(selectedAppointment.location === "In Person" ? (
					<InPersonConsultation
						isModalOpen={isConsultationModalOpen}
						handleCloseConsultationModal={() => setIsConsultationModalOpen(false)}
						appointment={selectedAppointment}
						patient={selectedPatient}
						setPatient={setSelectedPatient}
						practitioner={selectedTeamMember as TeamMember}
					/>
				) : (
					// A link failing the backend's segment guard yields "" — no room, rather than a junk one.
					<PractitionerVideoConsultation
						roomId={extractRoomId(selectedAppointment.consultation_link) ?? ""}
						isModalOpen={isConsultationModalOpen}
						handleCloseConsultationModal={() => setIsConsultationModalOpen(false)}
						patientId={selectedAppointment.patient_id || ""}
						appointment={selectedAppointment}
					/>
				))}
		</Layout>
	);
};

export default AppointmentManagementPage;
